import { useEffect, useState, useCallback } from "react";
import api, { apiErr, downloadFile, photoUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useWorkspace } from "@/context/WorkspaceContext";
import { TENDER } from "@/constants/testIds";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { StatusBadge } from "@/components/StatusBadge";
import CircularWastePanel from "@/components/CircularWastePanel";
import { fmtSGD } from "@/lib/format";
import {
  Plus, Award, Download, Sparkles, Loader2, CheckCircle2, FileText, Lock,
  MapPin, Camera, Bot, Building2, Target, ShieldCheck, Trophy, ListChecks, XCircle, Clock,
} from "lucide-react";
import { toast } from "sonner";

const OUTCOME_META = {
  won: { label: "Won", cls: "border-emerald-200 bg-emerald-50 text-emerald-800", Icon: Trophy },
  lost: { label: "Lost", cls: "border-rose-200 bg-rose-50 text-rose-700", Icon: XCircle },
  pending: { label: "Pending", cls: "border-amber-200 bg-amber-50 text-amber-800", Icon: Clock },
};

function OutcomeCard({ pack }) {
  const o = pack.outcome;
  const meta = o && OUTCOME_META[o.result];
  if (!meta) return null;
  const Icon = meta.Icon;
  const decision = o.decision_date ? o.decision_date : (o.decision_expected ? `Expected ${o.decision_expected}` : "—");
  const rank = o.our_rank ? `#${o.our_rank}${o.bidders ? ` of ${o.bidders}` : ""}` : (o.bidders ? `${o.bidders} bidders` : "—");
  const tiles = [
    { label: "Decision", value: decision },
    { label: "Award value", value: o.award_value_sgd ? fmtSGD(o.award_value_sgd) : "—" },
    { label: "Our rank", value: rank },
    { label: "Status", value: meta.label },
  ];
  return (
    <Card data-testid={TENDER.outcomeCard} className="border-sage-light p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="flex items-center gap-2 font-heading text-base font-semibold text-forest-dark"><Trophy className="h-4 w-4 text-forest-mid" /> Tender outcome</h3>
        <span className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-semibold ${meta.cls}`}><Icon className="h-3.5 w-3.5" /> {meta.label}</span>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {tiles.map((ti) => (
          <div key={ti.label} className="rounded-xl border border-sage-light bg-sage-surface p-3 text-center">
            <p className="font-heading text-sm font-bold text-forest-dark">{ti.value}</p>
            <p className="mt-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">{ti.label}</p>
          </div>
        ))}
      </div>
      {(o.reason || o.feedback) && (
        <div className={`mt-3 rounded-r-lg border-l-4 p-3 text-xs ${o.result === "lost" ? "border-rose-400 bg-rose-50/70 text-rose-900" : "border-emerald-400 bg-emerald-50/70 text-emerald-900"}`}>
          {o.reason || o.feedback}
        </div>
      )}
    </Card>
  );
}

export default function TenderPage() {
  const { hasRole } = useAuth();
  const ws = useWorkspace();
  const canManage = hasRole("tenant_admin", "contract_manager");
  const [packs, setPacks] = useState([]);
  const [active, setActive] = useState(null);
  const [intro, setIntro] = useState("");
  const [drafting, setDrafting] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", client_name: "", contract_ids: [], accent: "#27523C", company_tagline: "" });

  const loadList = useCallback(async () => {
    const r = await api.get("/tender-packs");
    setPacks(r.data);
    if (r.data[0] && !active) openPack(r.data[0].id);
  }, []); // eslint-disable-line

  useEffect(() => { loadList(); }, [loadList]);

  const openPack = async (id) => {
    const r = await api.get(`/tender-packs/${id}`);
    setActive(r.data);
    setIntro(r.data.intro || "");
  };

  const create = async () => {
    try {
      const { data } = await api.post("/tender-packs", {
        title: form.title, client_name: form.client_name, contract_ids: form.contract_ids,
        branding: { accent: form.accent, company_tagline: form.company_tagline },
      });
      toast.success("Tender pack created");
      setOpen(false);
      setForm({ title: "", client_name: "", contract_ids: [], accent: "#27523C", company_tagline: "" });
      await loadList();
      openPack(data.id);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const draftIntro = async () => {
    setDrafting(true);
    try {
      const { data } = await api.post(`/tender-packs/${active.id}/draft-intro`);
      setActive(data); setIntro(data.intro || "");
      toast.success("AI intro drafted — review and edit");
    } catch (e) { toast.error(apiErr(e, "AI drafting failed")); }
    finally { setDrafting(false); }
  };

  const saveIntro = async () => {
    try {
      const { data } = await api.patch(`/tender-packs/${active.id}`, { intro });
      setActive(data); toast.success("Saved");
    } catch (e) { toast.error(apiErr(e)); }
  };

  const markFinal = async () => {
    try {
      const { data } = await api.patch(`/tender-packs/${active.id}`, { status: "final" });
      setActive(data); loadList(); toast.success("Marked final");
    } catch (e) { toast.error(apiErr(e)); }
  };

  const exportPdf = () => downloadFile(`/tender-packs/${active.id}/export.pdf`, `tender-${active.title}.pdf`).catch(() => toast.error("Export failed"));

  const t = active?.assembled?.totals || {};
  const TOTAL_TILES = [
    { k: "contracts", label: "Contracts", icon: FileText },
    { k: "sites", label: "Sites", icon: MapPin },
    { k: "work_orders_completed", label: "Jobs completed", icon: CheckCircle2 },
    { k: "work_orders_verified", label: "Verified", icon: ShieldCheck },
    { k: "photos", label: "Evidence photos", icon: Camera },
    { k: "indicators_on_track", label: "On-target KPIs", icon: Target },
  ];

  return (
    <div className="space-y-5" data-testid={TENDER.root}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-heading text-2xl font-bold text-forest-dark">
            <Award className="h-6 w-6 text-forest-mid" /> Tender Packs
          </h1>
          <p className="text-sm text-muted-foreground">Reuse your captured, verified evidence to assemble a branded, tender-ready proposal.</p>
        </div>
        {canManage && <Button onClick={() => setOpen(true)} data-testid={TENDER.addBtn} className="bg-forest-mid hover:bg-forest-dark"><Plus className="mr-1.5 h-4 w-4" /> New tender pack</Button>}
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-4">
        {/* list */}
        <Card className="border-sage-light p-4 lg:col-span-1">
          <h3 className="mb-3 font-heading text-base font-semibold text-forest-dark">Your packs</h3>
          <div className="space-y-2">
            {packs.length === 0 && <p className="text-sm text-muted-foreground">No tender packs yet.</p>}
            {packs.map((p) => (
              <button key={p.id} onClick={() => openPack(p.id)} data-testid={TENDER.packRow(p.id)}
                className={`w-full rounded-lg border p-3 text-left transition-colors ${active?.id === p.id ? "border-forest-mid bg-sage-surface" : "hover:bg-sage-surface/60"}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate font-semibold text-forest-dark">{p.title}</span>
                  <StatusBadge status={p.status === "final" ? "released" : "draft"} />
                </div>
                <p className="mt-0.5 truncate text-xs text-muted-foreground">{p.client_name || "No client set"}</p>
                {p.outcome?.result && OUTCOME_META[p.outcome.result] && (() => {
                  const m = OUTCOME_META[p.outcome.result]; const I = m.Icon;
                  return (
                    <span data-testid={TENDER.outcomeBadge(p.id)} className={`mt-1 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${m.cls}`}>
                      <I className="h-3 w-3" /> {m.label}
                    </span>
                  );
                })()}
              </button>
            ))}
          </div>
        </Card>

        {/* detail */}
        <div className="space-y-4 lg:col-span-3">
          {!active ? (
            <Card className="border-dashed border-sage-light p-10 text-center text-sm text-muted-foreground">Select or create a tender pack.</Card>
          ) : (
            <>
              <Card className="border-sage-light p-5" style={{ borderTopColor: active.branding?.accent, borderTopWidth: 4 }}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h2 className="font-heading text-xl font-bold text-forest-dark">{active.title}</h2>
                    <p className="text-sm text-muted-foreground">
                      {active.client_name ? `Prepared for ${active.client_name}` : "No client set"}
                      {active.branding?.company_tagline ? ` · ${active.branding.company_tagline}` : ""}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="inline-block h-6 w-6 rounded-full border" style={{ backgroundColor: active.branding?.accent }} title="Brand accent" />
                    <StatusBadge status={active.status === "final" ? "released" : "draft"} />
                  </div>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={exportPdf} data-testid={TENDER.exportPdfBtn}><Download className="mr-1.5 h-4 w-4" /> Export PDF</Button>
                  {canManage && active.status !== "final" && <Button size="sm" onClick={markFinal} className="bg-forest-mid hover:bg-forest-dark" data-testid={TENDER.finalBtn}><Lock className="mr-1.5 h-4 w-4" /> Mark final</Button>}
                </div>
              </Card>

              <OutcomeCard pack={active} />

              {/* track record */}
              <Card className="border-sage-light p-5">
                <h3 className="mb-3 flex items-center gap-2 font-heading text-base font-semibold text-forest-dark"><Trophy className="h-4 w-4 text-forest-mid" /> Proven track record</h3>
                <div className="grid grid-cols-3 gap-3 sm:grid-cols-6">
                  {TOTAL_TILES.map((tile) => (
                    <div key={tile.k} className="rounded-xl border border-sage-light bg-sage-surface p-3 text-center">
                      <tile.icon className="mx-auto h-4 w-4 text-forest-mid" />
                      <p className="mt-1 font-heading text-2xl font-bold text-forest-dark">{t[tile.k] ?? 0}</p>
                      <p className="text-[10px] text-muted-foreground">{tile.label}</p>
                    </div>
                  ))}
                </div>
                {active.assembled?.indicator_highlights?.length > 0 && (
                  <div className="mt-4">
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Performance highlights</p>
                    <div className="flex flex-wrap gap-2">
                      {active.assembled.indicator_highlights.map((h, i) => (
                        <span key={i} className="flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs text-emerald-800">
                          <CheckCircle2 className="h-3.5 w-3.5" /> {h.name}: {h.latest}{h.unit} (target {h.target})
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </Card>

              {/* intro + AI */}
              <Card className="border-sage-light p-5">
                <div className="mb-3 flex items-center justify-between">
                  <h3 className="flex items-center gap-2 font-heading text-base font-semibold text-forest-dark"><FileText className="h-4 w-4 text-forest-mid" /> Tender introduction</h3>
                  {canManage && active.status !== "final" && (
                    <Button size="sm" variant="outline" onClick={draftIntro} disabled={drafting} data-testid={TENDER.draftIntroBtn}>
                      {drafting ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Sparkles className="mr-1.5 h-4 w-4 text-forest-mid" />} Draft with AI
                    </Button>
                  )}
                </div>
                {active.intro_ai && (
                  <div className="mb-3 flex items-start gap-2 rounded-r-lg border-l-4 border-amber-500 bg-amber-50/90 p-3">
                    <Bot className="mt-0.5 h-4 w-4 text-amber-600" />
                    <p className="text-xs text-amber-900"><b>AI-generated draft.</b> Review and edit before issuing to a client.</p>
                  </div>
                )}
                {active.status === "final" ? (
                  <div className="whitespace-pre-wrap rounded-lg bg-sage-surface p-4 text-sm text-forest-dark">{active.intro || "No introduction."}</div>
                ) : (
                  <>
                    <Textarea value={intro} onChange={(e) => setIntro(e.target.value)} rows={7}
                      placeholder="Write the opening of your bid, or generate it from your evidence with AI…" data-testid={TENDER.introInput} />
                    {canManage && <Button size="sm" className="mt-2 bg-forest-mid hover:bg-forest-dark" onClick={saveIntro} data-testid={TENDER.saveIntroBtn}>Save intro</Button>}
                  </>
                )}
              </Card>

              {/* gap action checklist (internal) */}
              {active.action_items?.length > 0 && (
                <Card className="border-sage-light p-5" data-testid={TENDER.checklist}>
                  <div className="mb-1 flex items-center justify-between gap-2">
                    <h3 className="flex items-center gap-2 font-heading text-base font-semibold text-forest-dark"><ListChecks className="h-4 w-4 text-forest-mid" /> Bid readiness action checklist</h3>
                    {typeof active.readiness_pct === "number" && (
                      <span className="rounded-full border border-forest-mid/30 bg-sage-surface px-2.5 py-1 text-xs font-semibold text-forest-dark">{active.readiness_pct}% ready</span>
                    )}
                  </div>
                  <p className="mb-3 text-xs text-muted-foreground">Internal to your bid team — the open requirements to evidence before you submit. Not included in the client PDF.</p>
                  <div className="space-y-2">
                    {active.action_items.map((it, i) => (
                      <div key={i} data-testid={TENDER.checklistItem(i)} className="flex items-start gap-3 rounded-lg border border-sage-light bg-white p-3">
                        <span className={`mt-0.5 shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${it.status === "Partial" ? "border-amber-200 bg-amber-50 text-amber-900" : "border-rose-200 bg-rose-50 text-rose-800"}`}>{it.status || "Gap"}</span>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-forest-dark">{it.label || it.requirement}</p>
                          {it.action && <p className="mt-0.5 text-xs text-muted-foreground">{it.action}</p>}
                        </div>
                        {it.work_order_id ? (
                          <span className="flex shrink-0 items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700"><CheckCircle2 className="h-3 w-3" /> WO raised</span>
                        ) : (
                          <span className="shrink-0 rounded-full bg-sage-surface px-2 py-0.5 text-[10px] font-semibold text-muted-foreground">No work order</span>
                        )}
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              {/* case studies */}
              <Card className="border-sage-light p-5">
                <h3 className="mb-3 font-heading text-base font-semibold text-forest-dark">Case studies <span className="text-xs font-normal text-muted-foreground">— drawn from verified work</span></h3>
                {(!active.assembled?.case_studies || active.assembled.case_studies.length === 0) ? (
                  <p className="text-sm text-muted-foreground">No completed/verified work orders found for the selected contracts yet.</p>
                ) : (
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    {active.assembled.case_studies.map((cs) => (
                      <div key={cs.work_order_id} className="rounded-xl border bg-white p-4">
                        <div className="flex items-start justify-between gap-2">
                          <p className="font-semibold text-forest-dark">{cs.title}</p>
                          <StatusBadge status={cs.status} />
                        </div>
                        <p className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground"><MapPin className="h-3 w-3" /> {cs.site_name}</p>
                        {cs.quantities?.length > 0 && <p className="mt-2 text-xs text-muted-foreground"><b>Delivered:</b> {cs.quantities.map((q) => `${q.quantity} ${q.unit} ${q.item}`).join(", ")}</p>}
                        {cs.notes && <p className="mt-1 text-xs text-muted-foreground">{cs.notes}</p>}
                        {cs.photos?.length > 0 && (
                          <div className="mt-2 grid grid-cols-4 gap-1.5">
                            {cs.photos.map((p) => <img key={p.id} src={photoUrl(p)} alt="" className="h-14 w-full rounded-md border object-cover" />)}
                          </div>
                        )}
                        {cs.pairs?.length > 0 && cs.pairs.map((pr) => (
                          <div key={pr.id} className="mt-2">
                            {pr.caption && <p className="text-[11px] text-muted-foreground">{pr.caption}</p>}
                            <div className="grid grid-cols-2 gap-1.5">
                              <div className="relative">{pr.before && <img src={photoUrl(pr.before)} alt="" className="h-16 w-full rounded-md border object-cover" />}<span className="absolute left-1 top-1 rounded bg-black/60 px-1 text-[8px] font-semibold text-white">BEFORE</span></div>
                              <div className="relative">{pr.after && <img src={photoUrl(pr.after)} alt="" className="h-16 w-full rounded-md border object-cover" />}<span className="absolute left-1 top-1 rounded bg-forest-mid px-1 text-[8px] font-semibold text-white">AFTER</span></div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </Card>

              <CircularWastePanel key={active.id} packId={active.id} canManage={canManage && active.status !== "final"} />
            </>
          )}
        </div>
      </div>

      {/* create dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[88vh] overflow-y-auto sm:max-w-lg" data-testid={TENDER.dialog}>
          <DialogHeader><DialogTitle className="font-heading">New tender pack</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Tampines Town Council Tender" data-testid={TENDER.field("title")} /></div>
            <div className="space-y-1.5"><Label>Prospective client</Label><Input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} data-testid={TENDER.field("client")} /></div>
            <div className="space-y-1.5">
              <Label>Reference contracts (evidence source)</Label>
              <div className="space-y-1.5 rounded-lg border p-3">
                {ws.contracts.length === 0 && <p className="text-xs text-muted-foreground">No contracts available. Add one in Setup & Data.</p>}
                {ws.contracts.map((c) => (
                  <label key={c.id} className="flex items-center gap-2 text-sm">
                    <Checkbox checked={form.contract_ids.includes(c.id)}
                      onCheckedChange={(ck) => setForm((f) => ({ ...f, contract_ids: ck ? [...f.contract_ids, c.id] : f.contract_ids.filter((x) => x !== c.id) }))}
                      data-testid={TENDER.field(`contract-${c.id}`)} />
                    <Building2 className="h-3.5 w-3.5 text-forest-mid" /> {c.name}
                  </label>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Brand accent</Label>
                <div className="flex items-center gap-2">
                  <input type="color" value={form.accent} onChange={(e) => setForm({ ...form, accent: e.target.value })} className="h-9 w-12 cursor-pointer rounded border" data-testid={TENDER.field("accent")} />
                  <Input value={form.accent} onChange={(e) => setForm({ ...form, accent: e.target.value })} className="h-9 font-mono text-xs" />
                </div>
              </div>
              <div className="space-y-1.5"><Label>Company tagline</Label><Input value={form.company_tagline} onChange={(e) => setForm({ ...form, company_tagline: e.target.value })} placeholder="Grounded. Proven." /></div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={create} disabled={!form.title} className="bg-forest-mid hover:bg-forest-dark" data-testid={TENDER.saveBtn}>Create pack</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
